import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag, CalendarDays } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useFetch } from "../../lib/hooks";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export function WorkCalendar() {
  const { data, reload } = useFetch("/app-settings/work-calendar");
  const [list, setList] = useState([]);
  const [labels, setLabels] = useState({});
  const [d, setD] = useState("");
  const [d2, setD2] = useState("");
  const [label, setLabel] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [cuti, setCuti] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) { setList(data.holidays); setLabels(data.labels); } }, [data]);
  const persist = async (dates, lbls) => {
    setBusy(true);
    try { const { data: r } = await api.put("/app-settings/work-calendar", { holidays: dates, labels: lbls }); setList(r.holidays); setLabels(r.labels); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const add = (dates, lbls) => persist([...new Set([...list, ...dates])].sort(), { ...labels, ...lbls });
  const addCompany = () => {
    if (!d) return;
    const dates = [];
    for (let x = new Date(d + "T00:00:00"), last = new Date((d2 && d2 >= d ? d2 : d) + "T00:00:00"); x <= last; x.setDate(x.getDate() + 1)) dates.push(x.toISOString().slice(0, 10));
    add(dates, Object.fromEntries(dates.map((x) => [x, label.trim() || "Libur perusahaan"])));
    setD(""); setD2(""); setLabel("");
  };
  const importNational = async () => {
    try {
      const { data: r } = await api.get(`/planning/holidays/national?year=${year}&include_cuti_bersama=${cuti}`);
      const rows = r.holidays.filter((h) => new Date(h.date + "T00:00:00").getDay() !== 0);
      await add(rows.map((h) => h.date), Object.fromEntries(rows.map((h) => [h.date, h.name])));
      toast[r.official ? "success" : "warning"](r.official ? `${rows.length} hari libur nasional ${year} ditambahkan` : r.note);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = (h) => { const l = { ...labels }; delete l[h]; persist(list.filter((x) => x !== h), l); };
  const isNational = (h) => labels[h] && !/perusahaan/i.test(labels[h]);
  const byYear = list.reduce((a, h) => { const y = h.slice(0, 4); (a[y] = a[y] || []).push(h); return a; }, {});
  return (
    <div className="surface p-5 lg:col-span-2" data-testid="work-calendar">
      <p className="font-heading font-bold text-sm mb-1 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-indigo-700" />Pengaturan Hari Kerja</p>
      <p className="text-xs text-slate-500 mb-3">Daftar hari libur kalender perusahaan (nasional + keputusan perusahaan). Tanggal di sini otomatis dikeluarkan dari perhitungan <b>Pagu Waktu</b> dan generate jadwal.</p>
      <div className="flex flex-wrap items-end gap-2 mb-2">
        <span className="text-xs font-semibold text-slate-600 pb-2">Libur nasional (SKB 3 Menteri):</span>
        <Input data-testid="calendar-year-input" type="number" className="h-9 w-24" value={year} onChange={(e) => setYear(e.target.value)} />
        <label className="flex items-center gap-1 text-xs text-slate-600 pb-2"><input data-testid="calendar-cuti-toggle" type="checkbox" checked={cuti} onChange={(e) => setCuti(e.target.checked)} /> termasuk cuti bersama</label>
        <Button type="button" size="sm" variant="outline" data-testid="calendar-import-national" disabled={busy || !year} onClick={importNational}><Flag className="h-3.5 w-3.5 mr-1" />Tambah libur nasional {year}</Button>
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <span className="text-xs font-semibold text-slate-600 pb-2">Libur perusahaan:</span>
        <Input data-testid="calendar-date-input" type="date" className="h-9 w-40" value={d} onChange={(e) => setD(e.target.value)} />
        <span className="text-xs text-slate-400 pb-2">s/d</span>
        <Input data-testid="calendar-date-end" type="date" className="h-9 w-40" value={d2} min={d} onChange={(e) => setD2(e.target.value)} />
        <Input data-testid="calendar-label-input" className="h-9 w-56" placeholder="Keterangan (mis. Family gathering)" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Button type="button" size="sm" variant="outline" data-testid="calendar-add-company" onClick={addCompany} disabled={!d || busy}>+ Libur perusahaan</Button>
      </div>
      {Object.keys(byYear).sort().map((y) => (
        <div key={y} className="mb-2" data-testid={`calendar-year-${y}`}>
          <p className="text-xs font-semibold text-slate-600 mb-1">{y} · {byYear[y].length} hari</p>
          <div className="flex flex-wrap gap-1">{byYear[y].map((h) => <button key={h} type="button" data-testid="calendar-chip" title={`${labels[h] || "Libur"} — klik untuk hapus`} className={`text-[11px] rounded border px-1.5 py-0.5 hover:border-red-500 hover:text-red-600 ${isNational(h) ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} onClick={() => remove(h)}><span className="font-mono">{h}</span>{labels[h] ? ` · ${labels[h]}` : ""} ×</button>)}</div>
        </div>
      ))}
      {!list.length && <p className="text-xs text-slate-400">Belum ada hari libur.</p>}
      {list.length > 0 && <p className="text-[11px] text-slate-400">{list.length} hari libur total · merah = nasional/cuti bersama, kuning = perusahaan · <button type="button" data-testid="calendar-clear" className="underline hover:text-red-600" onClick={() => { if (window.confirm("Hapus semua hari libur?")) persist([], {}); }}>hapus semua</button></p>}
    </div>
  );
}
